/**
 * Sunday sizes: one place that understands the store's size variants.
 *
 * Variant titles in the store look like `Queen 60"x78"x8"` (mattresses),
 * `Queen 60"x78"` (protector, topper) or `Standard 18"x27"` (pillow).
 * Everything here is read from those titles; no product is named in code.
 */

/* Widths that have a name in the wording file. */
const NAMED = [30, 36, 48, 60, 66, 72];

const RX = /(\d+(?:\.\d+)?)\s*["″]?\s*[x×]\s*(\d+(?:\.\d+)?)\s*["″]?(?:\s*[x×]\s*(\d+(?:\.\d+)?)\s*["″]?)?/i;

import { t } from '@sunday/text';

/* Size names by width come from the wording file (sunday.js.size: w60 'Queen size', t60 'Queen'),
   so they can be edited in admin and translated. A width with no name falls back to the variant's own. */
const named = (key) => (typeof document === 'undefined' ? '' : t(key));

/**
 * @param {{id:number,title:string,price:number,available:boolean}} v
 * @returns {{id:number,name:string,w:number,l:number,h:number|null,price:number,available:boolean}|null}
 */
export function parse(v) {
  const m = RX.exec(v.title || '');
  if (!m) return null;
  return {
    id: v.id,
    name: v.title.slice(0, m.index).trim(),
    w: Number(m[1]),
    l: Number(m[2]),
    h: m[3] ? Number(m[3]) : null,
    price: v.price,
    available: v.available !== false,
  };
}

export const parseAll = (variants) => variants.map(parse).filter(Boolean);

/**
 * Custom sizes we can cut, in inches: from the store facts record (layout/sunday.liquid prints
 * them as <script type="application/json" data-sunday-limits>), with these as a fallback.
 */
const LIM = (() => {
  try {
    return JSON.parse(document.querySelector('script[data-sunday-limits]').textContent) || {};
  } catch (e) {
    return {};
  }
})();
export const CUT = { minW: LIM.minW || 24, maxW: LIM.maxW || 72, minL: LIM.minL || 60, maxL: LIM.maxL || 84 };

/** Short names on the width tiles (the store's variants say "Large Queen"; the site says XL Queen). */
export function tileName(size) {
  return (NAMED.includes(size.w) && named(`size.t${size.w}`)) || size.name || `${size.w}″`;
}

/** Width name for people: 'Queen size', 'King size', or the variant's own name ('Standard'). */
export function widthName(size) {
  return (NAMED.includes(size.w) && named(`size.w${size.w}`)) || size.name || `${size.w}″`;
}

/** Length first, as people in India search: 'Queen size 78×60″'. */
export function label(size) {
  return `${widthName(size)} ${size.l}×${size.w}″`;
}

export const inches = (n) => `${Number(n)}″`;

/** Sorted unique values. */
export const uniq = (arr) => [...new Set(arr)].sort((a, b) => a - b);

/**
 * The standard size a custom one is cut from: the cheapest size at least as wide and as long,
 * at the chosen height. Ties go to the smaller piece of foam.
 */
export function cutFrom(sizes, w, l, h = null) {
  return (
    sizes
      .filter((s) => s.w >= w && s.l >= l && (h == null || s.h === h))
      .sort((a, b) => a.price - b.price || a.w * a.l - b.w * b.l)[0] || null
  );
}

export { money as rupees } from '@sunday/util';
